// Pick one of the platform's vector icons (the curated lucide set every
// surface can render — see @twasta/page-runtime/iconMap). Stores the icon's
// NAME, which `<PageIcon name=…>` resolves at render time.

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { ICON_NAMES, PageIcon, iconKey } from '@twasta/page-runtime/iconMap'

export default function IconPicker({ value, onChange, placeholder = 'Choose an icon', disabled }: {
  value?: string
  onChange: (name: string) => void
  placeholder?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)
  const current = iconKey(value)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const names = useMemo(() => {
    const s = q.trim().toLowerCase()
    return s ? ICON_NAMES.filter(n => n.includes(s)) : ICON_NAMES
  }, [q])

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
          background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 4,
          color: 'var(--color-text)', fontSize: '0.9375rem', cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
        }}
      >
        {current
          ? <><PageIcon name={current} size={16} strokeWidth={2} /><span>{current}</span></>
          : <span style={{ color: 'var(--color-text-secondary)' }}>{placeholder}</span>}
        <ChevronDown size={14} style={{ marginLeft: 'auto', opacity: 0.6 }} />
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute', zIndex: 50, top: 'calc(100% + 4px)', left: 0, right: 0,
            minWidth: 240, background: 'var(--color-bg)', border: '1px solid var(--color-border)',
            borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Search size={14} style={{ opacity: 0.6 }} />
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search icons"
              style={{
                flex: 1, padding: '4px 6px', background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)', borderRadius: 4, color: 'var(--color-text)',
                fontSize: '0.8625rem',
              }}
            />
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(34px, 1fr))', gap: 4,
            maxHeight: 220, overflowY: 'auto',
          }}>
            {names.map(n => (
              <button
                key={n}
                type="button"
                role="option"
                aria-selected={n === current}
                title={n}
                onClick={() => { onChange(n); setOpen(false); setQ('') }}
                style={{
                  height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 4, cursor: 'pointer', color: 'var(--color-text)',
                  border: n === current ? '1px solid var(--color-accent)' : '1px solid transparent',
                  background: n === current ? 'var(--color-bg-secondary)' : 'transparent',
                }}
              >
                <PageIcon name={n} size={16} strokeWidth={2} />
              </button>
            ))}
            {names.length === 0 && (
              <div style={{ gridColumn: '1 / -1', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', padding: 6 }}>
                No icon matches “{q}”.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
